import React, { useEffect, useState } from 'react';
import { Box, ListItemButton, Typography } from '@mui/material';
import Dialog, { type DialogProps } from './Dialog';

const NONE = 'none';

type SelectionKey = string | number | typeof NONE;

export interface ChooserDialogNoneOption {
  primary: string;
  secondary?: string;
}

/** Non-selectable row that runs `onClick` instead of becoming the Apply target. */
export interface ChooserDialogActionOption {
  primary: string;
  secondary?: string;
  onClick: () => void;
}

export interface ChooserDialogProps<T> {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  options: T[];
  getOptionKey: (option: T) => string | number;
  renderOption: (option: T, selected: boolean) => React.ReactNode;
  onSelect: (option: T) => void;
  /** Trailing option; Apply with it selected closes without calling onSelect. */
  noneOption?: ChooserDialogNoneOption;
  /** Shown above `noneOption`; click runs the action without selecting. */
  actionOption?: ChooserDialogActionOption;
  cancelText?: React.ReactNode;
  submitText?: React.ReactNode;
  maxWidth?: DialogProps['maxWidth'];
  /** Clears selection when this changes while open. */
  resetKey?: string | number;
}

function ChooserOption({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <ListItemButton
      role="option"
      aria-selected={selected}
      selected={selected}
      onClick={onClick}
      sx={{
        display: 'block',
        border: 1,
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 1.5,
        px: 1.5,
        py: 1.25,
        alignItems: 'flex-start',
        transition: (theme) =>
          theme.transitions.create(['border-color', 'background-color'], {
            duration: theme.transitions.duration.shorter,
          }),
      }}
    >
      {children}
    </ListItemButton>
  );
}

function ChooserDialog<T>({
  open,
  onClose,
  title,
  options,
  getOptionKey,
  renderOption,
  onSelect,
  noneOption,
  actionOption,
  cancelText = 'Cancel',
  submitText = 'Apply',
  maxWidth = 'xs',
  resetKey,
}: ChooserDialogProps<T>) {
  const [selectedKey, setSelectedKey] = useState<SelectionKey | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedKey(null);
    }
  }, [open, resetKey]);

  const apply = () => {
    if (selectedKey === NONE) {
      onClose();
      return;
    }
    const selected = options.find(
      (option) => getOptionKey(option) === selectedKey
    );
    if (selected !== undefined) {
      onSelect(selected);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      cancelText={cancelText}
      submitText={submitText}
      submitDisabled={selectedKey == null}
      onSubmitClick={apply}
      maxWidth={maxWidth}
    >
      <Box
        role="listbox"
        sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        {options.map((option) => {
          const key = getOptionKey(option);
          const selected = selectedKey === key;
          return (
            <ChooserOption
              key={key}
              selected={selected}
              onClick={() => setSelectedKey(key)}
            >
              {renderOption(option, selected)}
            </ChooserOption>
          );
        })}
        {actionOption ? (
          <ChooserOption selected={false} onClick={actionOption.onClick}>
            <Typography variant="body2" fontWeight={500} noWrap>
              {actionOption.primary}
            </Typography>
            {actionOption.secondary ? (
              <Box sx={{ mt: 0.25 }}>
                <Typography variant="caption" color="text.secondary">
                  {actionOption.secondary}
                </Typography>
              </Box>
            ) : null}
          </ChooserOption>
        ) : null}
        {noneOption ? (
          <ChooserOption
            selected={selectedKey === NONE}
            onClick={() => setSelectedKey(NONE)}
          >
            <Typography
              variant="body2"
              fontWeight={selectedKey === NONE ? 600 : 500}
              noWrap
            >
              {noneOption.primary}
            </Typography>
            {noneOption.secondary ? (
              <Box sx={{ mt: 0.25 }}>
                <Typography variant="caption" color="text.secondary">
                  {noneOption.secondary}
                </Typography>
              </Box>
            ) : null}
          </ChooserOption>
        ) : null}
      </Box>
    </Dialog>
  );
}

export default ChooserDialog;
